import { PublicKey } from '@solana/web3.js';

import { requestSolanaRpc } from './solana';

const TOKEN_PROGRAMS = new Set([
  'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',
  'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb',
]);

type MintResult = {
  value: {
    owner: string;
    executable: boolean;
    data: { parsed?: { type?: string; info?: { decimals?: unknown; isInitialized?: boolean } } };
  } | null;
};

export function parseSolanaMintDecimals(result: MintResult): number {
  const account = result.value;
  const parsed = account?.data?.parsed;
  const decimals = parsed?.info?.decimals;
  if (
    !account || !TOKEN_PROGRAMS.has(account.owner) || account.executable !== false ||
    parsed?.type !== 'mint' || parsed.info?.isInitialized !== true ||
    typeof decimals !== 'number' || !Number.isInteger(decimals) || decimals < 0 || decimals > 255
  ) {
    throw new Error('Token precision could not be verified');
  }
  return decimals;
}

const validDecimals = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 255;

export function resolveSolanaMintDecimals(
  chain: number | undefined, gecko: { address?: unknown; decimals?: unknown } | undefined,
  mint: string, canonical: number | undefined,
): number | undefined {
  if (validDecimals(chain)) return chain;
  if (gecko?.address === mint && validDecimals(gecko.decimals)) return gecko.decimals;
  return validDecimals(canonical) ? canonical : undefined;
}

export async function getSolanaMintDecimals(mint: string): Promise<number> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const address = new PublicKey(mint).toBase58();
    const result = await Promise.race([
      requestSolanaRpc<MintResult>('mint-metadata', 'getAccountInfo', [address, { encoding: 'jsonParsed', commitment: 'finalized' }]),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Token precision could not be verified')), 8000);
      }),
    ]);
    return parseSolanaMintDecimals(result);
  } catch {
    throw new Error('Token precision could not be verified');
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
